import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./ReelPaywall.module.scss"

/**
 * Reel — a non-onboarding Premium paywall built around a single truth most
 * vault paywalls skip: *photos are only half your memories.* Your videos — the
 * ones that actually move, breathe, make noise — deserve the same private,
 * full-quality home.
 *
 * The mechanic is a zoetrope, the Victorian optical drum that turns a strip of
 * still frames into motion. Here the strip is a band of your video memories
 * wrapped on a film drum behind a slit grille. You don't set a dial — you
 * *flick* it, and it spins on real momentum, coasting to rest against friction.
 * The faster it spins, the more the memory in the centre viewer comes to life:
 * it starts to play, the strip blurs into motion, the drum's glow warms from
 * cool to tungsten-amber. Let it slow and the frames settle back into crisp,
 * quietly-kept stills. Motion, made literal — and made yours.
 *
 * Divergence (vs. the ledger): a momentum flywheel (flick-and-coast physics),
 * not a value-setting dial; a zoetrope/film-drum motif; a warm cinematic
 * "your memories move" register; and the video/motion value lane that only one
 * prior paywall (Encore, a flat media player) has touched.
 *
 * Self-contained: renders on the built-in debug product data, reuses the
 * bundled `nimbus/` memory photos as clip thumbnails, no native injection.
 */

// Video memories, reused from the bundled photo set so the screen is fully
// self-contained (no runtime fetches). Durations are illustrative clip lengths,
// like a real library preview — not a claim.
const CLIPS: { file: string; dur: string }[] = [
  { file: "mem3.jpg", dur: "0:12" },
  { file: "mem7.jpg", dur: "0:06" },
  { file: "mem1.jpg", dur: "0:24" },
  { file: "mem9.jpg", dur: "0:08" },
  { file: "mem5.jpg", dur: "0:15" },
  { file: "mem12.jpg", dur: "0:04" },
  { file: "mem2.jpg", dur: "0:31" },
  { file: "mem14.jpg", dur: "0:09" },
  { file: "mem6.jpg", dur: "0:18" },
  { file: "mem10.jpg", dur: "0:07" },
  { file: "mem4.jpg", dur: "0:11" },
  { file: "mem16.jpg", dur: "0:05" },
]
const N = CLIPS.length

// Filmstrip geometry (logical px). STEP is one clip's advance.
const FRAME_W = 116
const GAP = 12
const STEP = FRAME_W + GAP
const STRIP_W = N * STEP

// Physics. Velocity is in px/s of strip travel; it decays each frame against a
// per-second friction factor. V_LIVE is the speed at which persistence-of-vision
// fully "kicks in" and the centre memory plays smoothly.
const FRICTION = 0.16 // fraction of velocity retained per second (heavy coast)
const V_LIVE = 900
const V_MAX = 2600

const mod = (a: number, n: number) => ((a % n) + n) % n
const clamp01 = (v: number) => Math.max(0, Math.min(1, v))

const Glyph = ({ name }: { name: "play" | "lock" | "close" | "arrow" }) => {
  if (name === "play")
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M8 5.2v13.6L19 12 8 5.2Z" fill="currentColor" />
      </svg>
    )
  if (name === "lock")
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect
          x="4.6"
          y="10.6"
          width="14.8"
          height="10.2"
          rx="3"
          fill="currentColor"
        />
        <path
          d="M8 10.6V7.8a4 4 0 0 1 8 0v2.8"
          stroke="currentColor"
          strokeWidth="2.1"
          fill="none"
          strokeLinecap="round"
        />
      </svg>
    )
  if (name === "arrow")
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M5 12h13m0 0-5-5m5 5-5 5"
          stroke="currentColor"
          strokeWidth="2.3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    )
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="m6 6 12 12M18 6 6 18"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      />
    </svg>
  )
}

const ReelPaywall = () => {
  const { state, actions } = useAppState()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true
  const trialDays = yearlyProduct?.introPricing?.period ?? 7
  const savePercent =
    yearlyProduct && monthlyProduct && monthlyProduct.price > 0
      ? Math.max(
          0,
          Math.round(
            (1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100,
          ),
        )
      : 79

  const [selected, setSelected] = useState<"yearly" | "monthly">("yearly")

  // Live physics state mirrored into React only where it drives the DOM we can't
  // reach imperatively (the centre memory + captions). The strip transform and
  // blur are written straight to the node each frame to stay smooth.
  const [centerIdx, setCenterIdx] = useState(2)
  const [live, setLive] = useState(0) // 0..1 liveliness for captions/glow

  const offsetRef = useRef(STEP * 2)
  const velRef = useRef(0)
  const liveRef = useRef(0)
  const rafRef = useRef(0)
  const lastTsRef = useRef(0)

  const stripRef = useRef<HTMLDivElement>(null)
  const stripBRef = useRef<HTMLDivElement>(null)
  const drumRef = useRef<HTMLDivElement>(null)

  const draggingRef = useRef(false)
  const touchedRef = useRef(false)
  const lastXRef = useRef(0)
  const lastMoveTsRef = useRef(0)

  // Tint the OS inset bars to blend with the dark screening room.
  useEffect(() => {
    actions.setHeaderInsetColor("#14121a")
    actions.setFooterInsetColor("#161320")
  }, [actions])

  const writeStrip = useCallback(() => {
    const o = mod(offsetRef.current, STRIP_W)
    const blur = Math.min(5.5, (liveRef.current - 0.12) * 8)
    const b = blur > 0 ? blur : 0
    for (const el of [stripRef.current, stripBRef.current]) {
      if (!el) continue
      el.style.transform = `translate3d(${-o}px,0,0)`
      el.style.filter = b ? `blur(${b.toFixed(2)}px)` : "none"
    }
    if (drumRef.current) {
      drumRef.current.style.setProperty("--live", liveRef.current.toFixed(3))
    }
  }, [])

  const loop = useCallback(
    (ts: number) => {
      if (!lastTsRef.current) lastTsRef.current = ts
      const dt = Math.min(0.05, (ts - lastTsRef.current) / 1000)
      lastTsRef.current = ts

      if (!draggingRef.current) {
        // Friction coast: velocity *= friction^dt.
        velRef.current *= Math.pow(FRICTION, dt)
        if (Math.abs(velRef.current) < 4) velRef.current = 0
        offsetRef.current += velRef.current * dt
      }

      // Liveliness eases toward the current speed's target so the "play" state
      // fades in/out smoothly instead of snapping.
      const target = clamp01(Math.abs(velRef.current) / V_LIVE)
      liveRef.current += (target - liveRef.current) * Math.min(1, dt * 6)
      if (liveRef.current < 0.004 && target === 0) liveRef.current = 0

      writeStrip()

      const idx = mod(Math.round(offsetRef.current / STEP), N)
      setCenterIdx((prev) => (prev === idx ? prev : idx))
      setLive((prev) =>
        Math.abs(prev - liveRef.current) > 0.02 ? liveRef.current : prev,
      )

      const spinning = draggingRef.current || Math.abs(velRef.current) >= 4
      if (spinning) {
        rafRef.current = requestAnimationFrame(loop)
      } else {
        rafRef.current = 0
        lastTsRef.current = 0
      }
    },
    [writeStrip],
  )

  const ensureLoop = useCallback(() => {
    if (!rafRef.current) {
      lastTsRef.current = 0
      rafRef.current = requestAnimationFrame(loop)
    }
  }, [loop])

  // One-time teaching flick: the drum spins in on its own and coasts to rest,
  // so the mechanic ("flick to spin") is immediately obvious.
  useLayoutEffect(() => {
    writeStrip()
    const kick = window.setTimeout(() => {
      if (touchedRef.current) return
      velRef.current = 1650
      ensureLoop()
    }, 520)
    return () => {
      window.clearTimeout(kick)
      cancelAnimationFrame(rafRef.current)
      rafRef.current = 0
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      touchedRef.current = true
      draggingRef.current = true
      lastXRef.current = e.clientX
      lastMoveTsRef.current = e.timeStamp
      velRef.current = 0
      ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
      ensureLoop()
    },
    [ensureLoop],
  )

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!draggingRef.current) return
    const dx = e.clientX - lastXRef.current
    const dt = Math.max(1, e.timeStamp - lastMoveTsRef.current)
    // Dragging right should feel like pulling the reel forward.
    offsetRef.current -= dx
    velRef.current = (-dx / dt) * 1000
    lastXRef.current = e.clientX
    lastMoveTsRef.current = e.timeStamp
  }, [])

  const endDrag = useCallback(
    (e: React.PointerEvent) => {
      if (!draggingRef.current) return
      draggingRef.current = false
      // Cap the fling so a violent swipe doesn't rocket off.
      velRef.current = Math.max(-V_MAX, Math.min(V_MAX, velRef.current))
      ;(e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId)
      ensureLoop()
    },
    [ensureLoop],
  )

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowRight" || e.key === "ArrowUp") {
        touchedRef.current = true
        velRef.current = Math.min(V_MAX, velRef.current + 620)
        ensureLoop()
        e.preventDefault()
      } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
        touchedRef.current = true
        velRef.current = Math.max(-V_MAX, velRef.current - 620)
        ensureLoop()
        e.preventDefault()
      }
    },
    [ensureLoop],
  )

  const buy = useCallback(() => {
    const product = selected === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
  }, [selected, yearlyProduct, monthlyProduct])

  const close = useCallback(() => native.postNativeEvent("close"), [])

  const isLive = live > 0.34
  const ctaLabel =
    selected === "yearly" && hasTrial
      ? `Try ${trialDays} days free`
      : "Keep them moving"

  const viewerCaption = useMemo(() => {
    if (isLive) return "Playing — full motion, full quality"
    return "Flick the reel · your videos come to life"
  }, [isLive])

  // The strip rendered twice, back-to-back, for seamless infinite spin.
  const renderStrip = (ref: React.RefObject<HTMLDivElement>) => (
    <div className={styles.strip} ref={ref}>
      {CLIPS.map((c, i) => (
        <div className={styles.frame} key={`${c.file}-${i}`}>
          <img
            src={`${process.env.PUBLIC_URL}/nimbus/${c.file}`}
            alt=""
            draggable={false}
            loading="eager"
          />
          <span className={styles.frameTag}>
            <Glyph name="play" />
            {c.dur}
          </span>
        </div>
      ))}
    </div>
  )

  return (
    <div className={styles.page}>
      <div className={styles.grain} aria-hidden="true" />
      <div className={styles.beam} aria-hidden="true" />

      <button className={styles.close} onClick={close} aria-label="Not now">
        <Glyph name="close" />
      </button>

      <header className={styles.intro}>
        <span className={styles.kicker}>
          <Glyph name="lock" />
          Private Photo Vault
        </span>
        <h1 className={styles.headline}>
          Your memories
          <span className={styles.moveWord}> move.</span>
        </h1>
        <p className={styles.sub}>
          Photos are only half the story. Keep every <strong>video</strong> in
          full motion and full quality — backed up, encrypted, and only yours.
        </p>
      </header>

      {/* The zoetrope: a film drum of your video memories you flick to spin. */}
      <section
        className={`${styles.stage} ${isLive ? styles.stageLive : ""}`}
        aria-hidden="true"
      >
        <div className={styles.drum} ref={drumRef}>
          <div className={styles.rail} />
          <div className={styles.window}>
            <div className={styles.stripTrack}>
              {renderStrip(stripRef)}
              {renderStrip(stripBRef)}
            </div>
            <div className={styles.slits} />
            <div className={styles.curveTop} />
            <div className={styles.curveBottom} />
          </div>
          <div className={styles.railBottom} />

          {/* The centre viewer lens — the memory that "plays" as you spin. */}
          <div className={`${styles.lens} ${isLive ? styles.lensLive : ""}`}>
            <img
              key={CLIPS[centerIdx].file}
              className={styles.lensImg}
              src={`${process.env.PUBLIC_URL}/nimbus/${CLIPS[centerIdx].file}`}
              alt=""
              draggable={false}
            />
            <div className={styles.lensSheen} />
            <span className={styles.lensState}>
              <Glyph name="play" />
              {isLive ? "LIVE" : CLIPS[centerIdx].dur}
            </span>
            <span className={styles.lensLock}>
              <Glyph name="lock" />
            </span>
          </div>
        </div>

        {/* The flick handle / drag surface. */}
        <div
          className={styles.crank}
          role="button"
          aria-label="Flick to spin the reel and bring your videos to life"
          tabIndex={0}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={onKeyDown}
        >
          <span className={styles.crankArrows}>
            <Glyph name="arrow" />
          </span>
          <span className={styles.crankLabel}>{viewerCaption}</span>
          <span className={styles.crankArrows} data-flip>
            <Glyph name="arrow" />
          </span>
        </div>
      </section>

      {/* Feature presentation as film "spec" chips, not a bullet list. */}
      <div className={styles.specs}>
        <span className={styles.spec}>Full motion</span>
        <span className={styles.specDot} />
        <span className={styles.spec}>Full-quality originals</span>
        <span className={styles.specDot} />
        <span className={styles.spec}>50 GB private Cloud Vault</span>
      </div>

      {/* Pricing — yearly featured, monthly demoted. */}
      <section className={styles.pricing}>
        <button
          className={`${styles.yearCard} ${
            selected === "yearly" ? styles.yearActive : ""
          }`}
          onClick={() => setSelected("yearly")}
          aria-pressed={selected === "yearly"}
        >
          <span className={styles.yearBadge}>
            BEST VALUE · SAVE {savePercent}%
          </span>
          <span className={styles.yearMain}>
            <span className={styles.yearName}>Yearly</span>
            <span className={styles.yearPrice}>{yearlyPrice}</span>
          </span>
          <span className={styles.yearSub}>
            <span>Just {yearlyPerMonth}/mo, billed yearly</span>
            <span>
              {hasTrial ? `${trialDays}-day free trial · ` : ""}every Premium
              feature
            </span>
          </span>
          <span className={styles.radio} aria-hidden="true" />
        </button>

        <button
          className={`${styles.monthLine} ${
            selected === "monthly" ? styles.monthActive : ""
          }`}
          onClick={() => setSelected("monthly")}
          aria-pressed={selected === "monthly"}
        >
          Prefer monthly? <strong>{monthlyPrice}/mo</strong>
        </button>

        <button className={styles.cta} onClick={buy}>
          <span>{ctaLabel}</span>
          <Glyph name="arrow" />
        </button>

        <p className={styles.fine}>
          {selected === "yearly" && hasTrial
            ? `${trialDays} days free, then ${yearlyPrice}/year. `
            : selected === "yearly"
              ? `${yearlyPrice}/year. `
              : `${monthlyPrice}/month. `}
          Auto-renews until canceled.
        </p>

        <div className={styles.legal}>
          <button onClick={() => native.postNativeEvent("already_purchased")}>
            Restore
          </button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span aria-hidden="true">·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </section>
    </div>
  )
}

export default ReelPaywall
